import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Search, Home as HomeIcon, GraduationCap, Briefcase, LineChart, Users, HandCoins, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { HERO_POSTER, IMG } from "@/lib/assets";
import { SectionHeading, ResourceCard } from "@/components/ResourceCard";
import { FounderTeaser } from "@/pages/Founder";

const PILLARS = [
  { icon: HandCoins, title: "Grants & Assistance", desc: "Verified funding and direct assistance pathways.", to: "/grants" },
  { icon: HomeIcon, title: "Housing & Homeownership", desc: "Down-payment help, counseling, and homebuyer readiness.", to: "/housing" },
  { icon: LineChart, title: "Financial Wellness", desc: "Credit, budgeting, and consumer rights education.", to: "/financial-wellness" },
  { icon: GraduationCap, title: "Scholarships & Education", desc: "Scholarships and pathways to higher education.", to: "/marketplace" },
  { icon: Briefcase, title: "Entrepreneurship", desc: "Technical assistance for small business owners.", to: "/marketplace" },
  { icon: Users, title: "Youth Mentorship", desc: "Mentoring and leadership for young people.", to: "/marketplace" },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/resources", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full pt-28 lg:pt-32 pb-0 lg:pb-16 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
          <div className="max-w-xl lg:max-w-2xl animate-fade-up pb-10 lg:pb-0">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-gold-light mb-6">
              <Sparkles className="h-4 w-4" /> A 501(c)(3) Community Foundation
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.02] tracking-tight">
              Opportunity<br />Starts Here.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-xl">
              Empowering individuals, youth, and families through education, mentorship, community resources, and pathways to opportunity.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/marketplace" data-testid="hero-explore-btn">
                <Button size="lg" className="rounded-full bg-gold hover:bg-gold-light text-white font-semibold px-8 h-14 text-base shadow-xl shadow-gold/30 w-full sm:w-auto">
                  <Search className="h-5 w-5 mr-2" /> Explore Opportunities
                </Button>
              </Link>
              <Link to="/donate" data-testid="hero-support-btn">
                <Button size="lg" variant="outline" className="rounded-full bg-white/5 border-white/40 text-white hover:bg-white hover:text-navy-900 font-semibold px-8 h-14 text-base backdrop-blur-sm w-full sm:w-auto">
                  <Heart className="h-5 w-5 mr-2" /> Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative flex justify-center lg:justify-end">
            <img
              src={HERO_POSTER}
              alt="Founder Carl Dorian Cheatham and his son"
              data-testid="hero-background-image"
              className="w-full max-w-md lg:max-w-lg h-auto rounded-t-3xl lg:rounded-3xl shadow-2xl ring-1 ring-white/10"
            />
          </div>
        </div>

        {/* Live metrics strip */}
        {stats && (
          <div className="relative bg-navy-900/70 backdrop-blur-md border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
              {[
                { v: `${stats.verified_resources}`, l: "Verified Resources Listed" },
                { v: `${stats.categories}`, l: "Opportunity Categories" },
                { v: stats.founded, l: "Founded" },
                { v: "501(c)(3)", l: "Registered Public Charity" },
              ].map((s, i) => (
                <div key={i} className="py-6 px-4 text-center lg:text-left">
                  <div className="font-display text-2xl lg:text-3xl font-bold text-white">{s.v}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
        <SectionHeading overline="What We Do" title="Pathways to real opportunity"
          subtitle="Explore nine categories of verified programs, education, and assistance — connecting our community to legitimate opportunities." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Link to={p.to} key={i} data-testid={`pillar-${i}`}
              className="card-hover group bg-white rounded-2xl border border-slate-200 p-7 flex flex-col">
              <div className="h-14 w-14 rounded-2xl bg-navy-900 text-gold-light flex items-center justify-center mb-5 group-hover:bg-gold group-hover:text-white transition-colors duration-300">
                <p.icon className="h-7 w-7" />
              </div>
              <h3 className="font-display text-xl font-bold text-navy-900">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{p.desc}</p>
              <span className="mt-4 text-sm font-semibold text-gold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Explore <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* MISSION SPLIT */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={IMG.community1} alt="Community volunteers packing supplies" className="rounded-3xl w-full h-[480px] object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -right-6 hidden sm:block bg-white rounded-2xl shadow-xl p-6 border border-slate-100 max-w-[220px]">
              <ShieldCheck className="h-8 w-8 text-verified mb-2" />
              <p className="text-sm font-semibold text-navy-900">Every listing verified before publication.</p>
            </div>
          </div>
          <div>
            <SectionHeading overline="Our Mission"
              title="A growing foundation, built for your community"
              subtitle="Millionaire Mindsetters Foundation connects individuals, youth, and families to education, assistance, and meaningful opportunities. We verify every program, clearly attribute third-party resources, and operate with full transparency as a registered 501(c)(3) public charity." />
            <div className="space-y-4">
              {["Verified, trustworthy resources — no affiliate funnels or hidden fees",
                "Clear separation of Foundation-operated vs. third-party programs",
                "Education-first approach to credit, housing, and wealth-building"].map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1 h-6 w-6 rounded-full bg-verified/10 text-verified flex items-center justify-center shrink-0"><ShieldCheck className="h-4 w-4" /></span>
                  <p className="text-slate-700">{t}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-block mt-8">
              <Button className="rounded-full bg-navy-900 hover:bg-navy-700 text-white px-7 h-12">Learn About Our Mission</Button>
            </Link>
          </div>
        </div>
      </section>

      <FounderTeaser />

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <SectionHeading overline="Featured Programs" title="Opportunities worth exploring"
              subtitle="A curated selection from our verified Opportunity Marketplace." />
            <Link to="/marketplace"><Button variant="outline" className="rounded-full">View all <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featured.map((r) => <ResourceCard key={r.id} r={r} />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden bg-navy-900 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-white leading-tight">Help us open more doors.</h2>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl mx-auto">Your support funds mentorship, housing readiness, and direct assistance for families across our community.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate"><Button size="lg" className="rounded-full bg-gold hover:bg-gold-light text-white font-semibold px-8 h-14">Donate Today</Button></Link>
            <Link to="/volunteer"><Button size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-navy-900 px-8 h-14">Become a Volunteer</Button></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
